import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Tag, TagGroupColor } from "./types";
import { TAG_GROUP_COLORS } from "./types";

export interface SelectedTagChipProps {
  /** The tag to display */
  tag: Tag;
  /** Color theme for the chip */
  color: TagGroupColor;
  /** Callback when remove button is clicked */
  onRemove: (tagId: string) => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Color-coded pill chip showing a selected tag with remove functionality.
 *
 * @example
 * ```tsx
 * <SelectedTagChip
 *   tag={{ id: 'project-123', label: 'Building A', count: 5 }}
 *   color="blue"
 *   onRemove={(id) => handleRemove(id)}
 * />
 * ```
 */
export function SelectedTagChip({ tag, color, onRemove, className }: SelectedTagChipProps) {
  const colors = TAG_GROUP_COLORS[color];

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(tag.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onRemove(tag.id);
    }
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border-0 px-2.5 py-0.5 text-xs font-normal shadow-none outline-none ring-0 transition-colors",
        colors.chip,
        colors.chipText,
        className
      )}
      role="listitem"
    >
      <span className="max-w-[150px] truncate">
        {tag.label}
        {tag.count > 0 && <span className="ml-1 opacity-70">({tag.count})</span>}
      </span>
      <button
        type="button"
        onClick={handleRemove}
        onKeyDown={handleKeyDown}
        className={cn(
          "ml-0.5 rounded-full p-0.5 transition-colors",
          "hover:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
          "focus-visible:ring-current"
        )}
        aria-label={`Remove ${tag.label} filter`}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
}
